import React from 'react';

import { Chip } from '@crehana/ui';
import { useMediaQuery } from '@crehana/react-hooks';
import { useTranslation } from '@crehana/i18n';

import useCatalogCourseDuration from 'Jsx/Catalog/hooks/useCatalogCourseDuration';

import { useAppContext } from '../../context/AppContext';
import {
  getLevelsSelected,
  getSelectedDurations,
  getSelectedLanguages,
  getSoftwaresSelected,
} from '../FiltersDesktop/utils';
import BorderContainer from '../BorderContainer';

interface FilterChipsProps {
  hideTitle?: boolean;
  hideCleanButton?: boolean;
  onFiltersChange?: (totalFilters: number) => void;
}

const FilterChips: React.FC<FilterChipsProps> = ({
  hideTitle = false,
  hideCleanButton = false,
  onFiltersChange,
}) => {
  const { t } = useTranslation();
  const { matches: isSm } = useMediaQuery('sm');
  const { getDurationByTag } = useCatalogCourseDuration();
  const { filters, filterActions } = useAppContext();
  const levelsSelected = getLevelsSelected(filters);
  const selectedLanguages = getSelectedLanguages(filters);
  const softwaresSelected = getSoftwaresSelected(filters);
  const durationSelected = getSelectedDurations(filters);

  const totalFiltersSelected =
    levelsSelected.length +
    selectedLanguages.length +
    softwaresSelected.length +
    durationSelected.length;
  const hasFilterTagsToShow = totalFiltersSelected > 0;

  React.useEffect(() => {
    onFiltersChange?.(totalFiltersSelected);
  }, [totalFiltersSelected]);

  const renderClearFilters = () => {
    const title =
      totalFiltersSelected > 1
        ? `Borrar ${totalFiltersSelected} filtros`
        : 'Borrar filtro';

    return (
      <button
        type="button"
        className="font-subtitle4 text-primary-main"
        onClick={filterActions.clearAllFilters}
        title={title}
      >
        {t('CATALOG_CLEAN_FILTERS')} ({totalFiltersSelected})
      </button>
    );
  };

  if (!hasFilterTagsToShow) {
    return null;
  }

  return (
    <BorderContainer hideBorderTop noPaddingBottom>
      {isSm && (
        <div className="flex items-center justify-between mb-16">
          <span className="uppercase text-gray-dark text-12">
            {t('CATALOG_APPLIED_FILTERS')}
          </span>
          {renderClearFilters()}
        </div>
      )}

      {!hideTitle && !isSm && (
        <h2 className="uppercase text-gray-dark text-12 tw-font-semibold my-12">
          {t('CATALOG_APPLIED_FILTERS')}
        </h2>
      )}

      <div className="flex items-center flex-wrap w-full mb-16">
        {levelsSelected.map(level => (
          <Chip
            key={`levels-${level.id}`}
            label={level.name}
            onDelete={() => filterActions.toggleFilterLevel(level.id)}
            className="mr-8 mb-8"
          />
        ))}
        {softwaresSelected.map(software => (
          <Chip
            key={`softwares-${software.id}`}
            label={software.name}
            onDelete={() => filterActions.toggleFilterSoftware(software.id)}
            className="mr-8 mb-8"
          />
        ))}
        {selectedLanguages.map(language => (
          <Chip
            key={`languages-${language.id}`}
            label={language.name}
            onDelete={() => filterActions.toggleFilterLanguage(language.id)}
            className="mr-8 mb-8"
          />
        ))}
        {durationSelected.map(durationTag => {
          const duration = getDurationByTag(durationTag);

          if (!duration) {
            return null;
          }

          return (
            <Chip
              key={`duration-${duration.tag}`}
              label={duration.description!}
              onDelete={() => filterActions.toggleFilterDuration(duration.tag!)}
              className="mr-8 mb-8"
            />
          );
        })}
      </div>

      {!hideCleanButton && !isSm && renderClearFilters()}
    </BorderContainer>
  );
};

export default FilterChips;
